import { authClient } from "./auth-client";

/**
 * Le passkey viste dal frontend.
 *
 * Ogni chiamata qui dentro può fallire perché **l'utente ha annullato** il
 * dialogo di sistema — che non è un errore da mostrare in rosso, è una scelta.
 * Distinguere quel caso dal guasto vero è l'unica ragione per cui questo file
 * esiste invece di chiamare il client direttamente.
 */

export interface PasskeyOutcome {
  ok: boolean;
  /** `true` quando è stato l'utente a chiudere il dialogo: nessun messaggio. */
  cancelled: boolean;
  error?: string;
}

/** WebAuthn segnala l'annullamento e il timeout con `NotAllowedError`. */
function isCancellation(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "NotAllowedError" || error.name === "AbortError")
  );
}

function describe(error: unknown): string {
  if (error instanceof DOMException) {
    switch (error.name) {
      case "InvalidStateError":
        return "Questo dispositivo ha già una passkey per Qota.";
      case "SecurityError":
        return "Il dominio non è valido per le passkey. Usa localhost, non 127.0.0.1.";
      default:
        return error.message || "Operazione non riuscita.";
    }
  }

  return error instanceof Error ? error.message : "Operazione non riuscita.";
}

/** Accesso con passkey. `autoFill` la propone dentro il campo di testo. */
export async function signInWithPasskey(options?: { autoFill?: boolean }): Promise<PasskeyOutcome> {
  try {
    const result = await authClient.signIn.passkey({ autoFill: options?.autoFill ?? false });

    if (result?.error) {
      return {
        ok: false,
        cancelled: false,
        error: result.error.message ?? "Accesso non riuscito.",
      };
    }

    return { ok: true, cancelled: false };
  } catch (error) {
    if (isCancellation(error)) return { ok: false, cancelled: true };
    return { ok: false, cancelled: false, error: describe(error) };
  }
}

/** Registra una passkey sul dispositivo attuale. Richiede una sessione attiva. */
export async function registerPasskey(name: string): Promise<PasskeyOutcome> {
  try {
    const result = await authClient.passkey.addPasskey({ name });

    if (result?.error) {
      return {
        ok: false,
        cancelled: false,
        error: result.error.message ?? "Registrazione non riuscita.",
      };
    }

    return { ok: true, cancelled: false };
  } catch (error) {
    if (isCancellation(error)) return { ok: false, cancelled: true };
    return { ok: false, cancelled: false, error: describe(error) };
  }
}

export interface StoredPasskey {
  id: string;
  name?: string | null;
  deviceType: string;
  createdAt: string | Date | null;
}

export async function listPasskeys(): Promise<StoredPasskey[]> {
  const result = await authClient.passkey.listUserPasskeys();
  return (result.data ?? []) as StoredPasskey[];
}

export async function deletePasskey(id: string): Promise<void> {
  await authClient.passkey.deletePasskey({ id });
}

/**
 * Un nome leggibile per il dispositivo su cui si sta registrando.
 * Serve a distinguere «iPhone di Giulia» da «MacBook» nell'elenco: senza,
 * togliere la passkey giusta diventa un indovinello.
 */
export function guessDeviceName(): string {
  const ua = navigator.userAgent;

  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";

  return "Questo dispositivo";
}
